import { useState } from 'react';
import { motion } from 'framer-motion';

type SlotStatus = 'available' | 'limited' | 'busy' | 'unavailable';

interface TimeSlot {
  time: string;
  status: SlotStatus;
  vehiclesAvailable: number;
}

interface DayAvailability {
  date: Date;
  status: SlotStatus;
  slots: TimeSlot[];
}

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

function generateAvailability(year: number, month: number): DayAvailability[] {
  const days: DayAvailability[] = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const isPast = date < today;
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isFriday = dayOfWeek === 5;

    // Simuler la disponibilité de manière réaliste
    const seed = (d * 7 + month * 31 + year) % 100;
    let dayStatus: SlotStatus;

    if (isPast) {
      dayStatus = 'unavailable';
    } else if (isWeekend || isFriday) {
      dayStatus = seed < 40 ? 'limited' : seed < 70 ? 'busy' : 'available';
    } else {
      dayStatus = seed < 60 ? 'available' : seed < 85 ? 'limited' : 'busy';
    }

    const timeSlots: TimeSlot[] = [];
    const slotTimes = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];

    slotTimes.forEach((time) => {
      const hour = parseInt(time.split(':')[0]);
      const slotSeed = (d * 13 + hour * 7 + month * 3) % 100;
      let slotStatus: SlotStatus;
      let vehicles: number;

      if (isPast) {
        slotStatus = 'unavailable';
        vehicles = 0;
      } else if ((isWeekend || isFriday) && (hour >= 8 && hour <= 18)) {
        slotStatus = slotSeed < 30 ? 'available' : slotSeed < 60 ? 'limited' : 'busy';
        vehicles = slotStatus === 'available' ? 3 : slotStatus === 'limited' ? 1 : 0;
      } else {
        slotStatus = slotSeed < 50 ? 'available' : slotSeed < 80 ? 'limited' : 'busy';
        vehicles = slotStatus === 'available' ? 3 : slotStatus === 'limited' ? 2 : 0;
      }

      timeSlots.push({ time, status: slotStatus, vehiclesAvailable: vehicles });
    });

    days.push({ date, status: dayStatus, slots: timeSlots });
  }

  return days;
}

export default function AvailabilityCalendar() {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState<DayAvailability | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  const availability = useMemo(
    () => generateAvailability(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDay(null);
    setSelectedSlot(null);
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDay(null);
    setSelectedSlot(null);
  };

  const canGoPrev = () => {
    const now = new Date();
    return !(currentYear === now.getFullYear() && currentMonth === now.getMonth());
  };

  const getStatusColor = (status: SlotStatus) => {
    switch (status) {
      case 'available': return 'bg-emerald-500';
      case 'limited': return 'bg-amber-500';
      case 'busy': return 'bg-red-400';
      case 'unavailable': return 'bg-slate-300';
    }
  };

  const getStatusBg = (status: SlotStatus) => {
    switch (status) {
      case 'available': return 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100';
      case 'limited': return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
      case 'busy': return 'bg-red-50 border-red-200';
      case 'unavailable': return 'bg-slate-50 border-slate-200';
    }
  };

  const getStatusLabel = (status: SlotStatus) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'limited': return 'Limité';
      case 'busy': return 'Complet';
      case 'unavailable': return 'Indisponible';
    }
  };

  const isToday = (date: Date) => {
    const now = new Date();
    return date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  };

  return (
    <section className="mb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-night-blue to-slate-800 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-voyage-gold/20 rounded-xl flex items-center justify-center">
              <i className="ri-calendar-check-line text-voyage-gold text-2xl"></i>
            </div>
            <div>
              <h2 className="font-playfair text-2xl font-bold text-white">Disponibilités en temps réel</h2>
              <p className="text-slate-400 font-inter text-sm">Consultez nos créneaux disponibles</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-6">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
              <i className="ri-information-line text-voyage-gold"></i>
              <span className="font-inter text-sm text-white">Cliquez sur un jour pour voir les horaires</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {[
                { status: 'available', label: 'Disponible' },
                { status: 'limited', label: 'Places limitées' },
                { status: 'busy', label: 'Complet' },
              ].map((item) => (
                <div key={item.status} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(item.status)}`}></div>
                  <span className="font-inter text-xs text-night-blue/80 font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
